import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from '../api/client'
import { Loading } from '../components/ui'
import { DashboardSEO } from '../hooks/useSEO'
import type { Job } from '../types'

export default function SubmitWorkPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const [job, setJob] = useState<Job | null>(null)
  const [content, setContent] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchJob()
  }, [jobId])

  const fetchJob = async () => {
    try {
      const data = await api.get<Job>(`/jobs/${jobId}`)
      setJob(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (content.length < 10) {
      setError('Submission must be at least 10 characters')
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      await api.post(`/submissions/jobs/${jobId}/submit`, { content })
      navigate('/my-work')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) return <Loading message="Loading job..." />

  return (
    <div>
      <DashboardSEO title="Submit Work" />
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/my-work" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to My Work
        </Link>
      </div>

      {job && (
        <div className="card-static" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: '0 0 0.5rem' }}>{job.title}</h2>
              <p className="text-secondary" style={{ margin: 0 }}>
                Submit your completed work for review
              </p>
            </div>
            <span className="usdc-amount">${job.reward} USDC</span>
          </div>
        </div>
      )}

      <div className="card-static" style={{ maxWidth: 800 }}>
        <h3 style={{ marginBottom: '1rem' }}>Submit Work</h3>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="label" htmlFor="content">Your Submission</label>
            <textarea
              id="content"
              className="input textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your completed work, code, or deliverables here..."
              style={{ minHeight: 300 }}
              disabled={isSubmitting}
            />
            <p className="form-hint">
              Provide all deliverables and any relevant notes. You can include code, links, or detailed explanations.
            </p>
          </div>

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
          </button>
        </form>
      </div>
    </div>
  )
}
